import { CategoryBasicDto, ClassifierFormDataType } from '../classifier';
import { z } from 'zod/v3';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ClassifierFormDataSchema } from '../classifier/schema/formData/ClassifierFormDataSchema';
import { EntitySchema } from '../common/utils/schema/EntitySchema';
import translations from './types.translations.json';
import {
  zCode,
  zName,
  zDescription,
  zValidPeriod,
  transformNull,
  transformToValidPeriod,
  zIsoDate,
  formatCodeName,
  getDayjs,
} from '../common/utils';
import { ClassifierMappingSearchResultSchema } from './schema/ClassifierMappingSearchResultSchema';

const { addTranslations, t } = i18nNsFactory('category-mapping-types');
addTranslations(translations);

export interface ClassifierMappingDto {
  id?: number | null;
  code: string;
  name: string;
  description?: string | null;
  classifierFrom?: Omit<
    ClassifierFormDataType,
    'categories' | 'categoryGroups' | 'combinedGroups' | 'inUse' | 'hierarchy'
  >;
  classifierTo?: Omit<
    ClassifierFormDataType,
    'categories' | 'categoryGroups' | 'combinedGroups' | 'inUse' | 'hierarchy'
  >;
  categoryMappings: CategoryMappingDto[];
}

export interface CategoryMappingDto {
  id?: number | null;
  description?: string | null;
  validFrom: string | null;
  validTo: string | null;
  categoryIdFrom: number;
  categoryFrom?: Omit<
    CategoryBasicDto,
    | 'childSequenceNumber'
    | 'levelNumber'
    | 'parentCode'
    | 'validFrom'
    | 'validTo'
  >;
  categoryIdTo: number;
  categoryTo?: Omit<
    CategoryBasicDto,
    | 'childSequenceNumber'
    | 'levelNumber'
    | 'parentCode'
    | 'validFrom'
    | 'validTo'
  >;
}

export const TimePeriodSchema = z.tuple([
  z.string().nullable(),
  z.string().nullable(),
]);
export type TimePeriodType = z.infer<typeof TimePeriodSchema>;

const MappedCategorySchema = transformToValidPeriod(
  z.object({
    id: z.number().nullable(),
    code: transformNull<string>(z.string(), ''),
    name: transformNull<string>(z.string(), ''),
    nameEng: transformNull<string>(z.string(), ''),
    description: transformNull<string>(z.string(), ''),
    categoryType: transformNull<string>(z.string(), ''),
    sequenceNumber: transformNull<number>(z.number(), '' as any),
    validFrom: zIsoDate().nullable(),
    validTo: zIsoDate().nullable(),
  }),
);

export const CategoryMappingSchema = z
  .object({
    id: z.number().nullish(),
    description: zDescription().nullish(),
    categoryIdFrom: z.number({
      errorMap: () => ({
        message: t('validation.category-from'),
      }),
    }),
    categoryFrom: MappedCategorySchema.nullable(),
    categoryIdTo: z.number({
      errorMap: () => ({
        message: t('validation.category-to'),
      }),
    }),
    categoryTo: MappedCategorySchema.nullable(),
    validPeriod: zValidPeriod().optional(),
  })
  .transform(({ categoryFrom, categoryTo, ...rest }) => ({
    codeNameFrom: formatCodeName(categoryFrom),
    codeNameTo: formatCodeName(categoryTo),
    categoryFrom,
    categoryTo,
    ...rest,
  }));

export const CategoryMappingValidFormDataSchema =
  CategoryMappingSchema.innerType().omit({
    categoryFrom: true,
    categoryTo: true,
  });

export type CategoryMappingType = z.infer<typeof CategoryMappingSchema>;

const MappedClassifierFormDataSchema = z.union([
  transformToValidPeriod(
    ClassifierFormDataSchema.options[0].innerType().innerType().omit({
      categories: true,
      categoryGroups: true,
      combinedGroups: true,
      inUse: true,
    }),
  ),
  transformToValidPeriod(
    ClassifierFormDataSchema.options[1].innerType().innerType().omit({
      categories: true,
      categoryGroups: true,
      combinedGroups: true,
      inUse: true,
    }),
  ),
]);

export const ClassifierMappingsSchema = z.object({
  id: z.number().nullish(),
  code: zCode(),
  name: zName({
    errorMap: () => ({
      message: t('validation.name'),
    }),
  }).min(1),
  description: zDescription().nullish(),
  classifierIdFrom: z.number({
    errorMap: () => ({
      message: t('validation.classifier-from'),
    }),
  }),
  classifierFrom: MappedClassifierFormDataSchema.nullable(),
  classifierIdTo: z
    .number({
      errorMap: () => ({
        message: t('validation.classifier-to'),
      }),
    })
    .min(1),
  classifierTo: MappedClassifierFormDataSchema.nullable(),
  categoryMappings: z.array(CategoryMappingSchema),
});

export const ClassifierMappingsFormDataSchema = ClassifierMappingsSchema.omit({
  code: true,
})
  .extend({
    code: z.string(),
    id: z.number().nullable(),
  })
  .merge(EntitySchema.omit({ id: true }));

export const ClassifierMappingsValidFormDataSchema =
  ClassifierMappingsSchema.omit({
    classifierFrom: true,
    classifierTo: true,
    categoryMappings: true,
  }).extend({
    categoryMappings: z.array(CategoryMappingValidFormDataSchema),
  });

export type ClassifierMappingsFormDataType = z.infer<
  typeof ClassifierMappingsFormDataSchema
>;

export type ClassifierMappingsType = z.infer<typeof ClassifierMappingsSchema>;

export const ClassifierMappingsDtoSchema =
  ClassifierMappingsValidFormDataSchema.transform((val) => ({
    ...val,
    categoryMappings: val.categoryMappings.map((mapping) => {
      const { validPeriod, ...rest } = mapping;
      return {
        ...rest,
        validFrom: getDayjs(validPeriod?.[0])?.format('YYYY-MM-DD') || null,
        validTo: getDayjs(validPeriod?.[1])?.format('YYYY-MM-DD') || null,
      };
    }),
  }));

export const ClassifierMappingOverviewSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  classifierFromId: z.number(),
  classifierFromCode: z.string(),
  classifierFromName: z.string(),
  classifierToId: z.number(),
  classifierToCode: z.string(),
  classifierToName: z.string(),
});

export type ClassifierMappingOverviewDto = z.infer<
  typeof ClassifierMappingOverviewSchema
>;

export type ClassifierMappingSearchResultType = z.infer<
  typeof ClassifierMappingSearchResultSchema
>;
